import { Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { map } from 'rxjs/operators'
import * as lodash from 'lodash'
import {
  ChannelReviewFetchParams,
  ChannelReviewImportPayload,
  ChannelReviewItem,
  ChannelReviewSearchParams,
  ChannelReviewsResponse,
  AirbnbAIGuestReviewPayload,
  ChannelReviewSources,
  AirbnbAIGuestReviewResponsePayload,
  ReviewRatingCategory,
} from './model'
import { AirbnbHost2GuestReviewPayload, VrboHost2GuestReviewPayload } from '@tokeet-frontend/channels'
import { ReviewsInsights } from './insight'

@Injectable()
export class ReviewService {
  constructor(private http: HttpClient) {}

  getReviews(params?: ChannelReviewFetchParams) {
    return this.http
      .get<{ response: ChannelReviewsResponse }>('@api/v1/reviews', { params: params as any })
      .pipe(map((data) => data.response))
  }

  importReviews(payload: ChannelReviewImportPayload) {
    return this.http.post('@api/v1/reviews/import', payload)
  }

  updateReview(id: string, payload: Partial<ChannelReviewItem>) {
    return this.http
      .put<{ response: ChannelReviewItem }>(`@api/v1/reviews/${id}`, payload)
      .pipe(map((data) => data.response))
  }

  getBookingReviews(bookingId: string, sources = [ChannelReviewSources.Airbnb]) {
    return this.searchReviews({
      inquiries: [bookingId],
      sources,
      includeUnsubmittedByHost: true,
    }).pipe(
      map((data) => ({
        reviewFromGuest: data.reviews.find((r) => r.reviewer.role === 'guest'),
        reviewToGuest: data.reviews.find((r) => r.reviewer.role === 'host'),
      }))
    )
  }

  searchReviews(params?: ChannelReviewSearchParams) {
    return this.http
      .post<{ response: ChannelReviewsResponse }>('@api/v1/reviews/search', { ...params })
      .pipe(map((data) => data.response))
  }

  getReviewStats(params?: ChannelReviewSearchParams) {
    return this.http
      .post<{ response: ChannelReviewsResponse }>('@api/v1/reviews/stats', params)
      .pipe(map((data) => data.response))
  }

  getRatingCategories() {
    return this.http.get<{ response: string[] }>('@api/v1/reviews/rating-categories').pipe(
      map((data) => data.response || []),
      map((response) =>
        response.map((category) => ({ value: category, name: lodash.startCase(category) } as ReviewRatingCategory))
      )
    )
  }

  getRentalReviewInsights(rentalId: string) {
    return this.http
      .post<{ insights: ReviewsInsights }>(`@api/ai/responder/review/insights`, { rental_id: rentalId })
      .pipe(map((data) => data.insights))
  }

  aiGuestReview(payload: AirbnbAIGuestReviewPayload) {
    return this.http.post<{ message: string }>(`@api/ai/responder/review`, payload).pipe(map((data) => data.message))
  }

  aiGuestReviewResponse(payload: AirbnbAIGuestReviewResponsePayload) {
    return this.http
      .post<{ message: string }>(`@api/ai/responder/review/reply`, payload)
      .pipe(map((data) => data.message))
  }

  respondReview(reviewId: string, text: string) {
    return this.http
      .post<{ response: ChannelReviewItem }>(`@api/v1/reviews/${reviewId}/respond`, { response: text })
      .pipe(map((data) => data.response))
  }

  reviewAirbnbGuest(confirmationCode: string, payload: AirbnbHost2GuestReviewPayload) {
    const url = `@api/v1/reviews/airbnb/host-to-guest/${confirmationCode}`
    return this.http.post<{ response: ChannelReviewItem }>(url, payload).pipe(map((data) => data.response))
  }

  reviewVrboGuest(confirmationCode: string, payload: VrboHost2GuestReviewPayload) {
    const url = `@api/v1/reviews/vrbo/host-to-guest/${confirmationCode}`
    return this.http.post<{ response: ChannelReviewItem }>(url, payload).pipe(map((data) => data.response))
  }

  getReview(revId: string) {
    return this.http.get<{ response: ChannelReviewItem }>(`@api/v1/reviews/${revId}`).pipe(map((data) => data.response))
  }
}
